import { openExternalUrl } from './open-external-url';

/**
 * The Meeting Information field accepts either an http(s) link OR a phone number
 * (see `MEETING_LINK_URL_OR_PHONE_REGEX`). Turn a join target into an openable
 * href: leave real links alone, but wrap a phone number in `tel:` so clicking
 * "Join" dials it instead of opening a useless new tab.
 *
 * Returns `undefined` for anything that is neither — a bare `javascript:` or
 * `mailto:` value yields no digits and must not be handed to the browser.
 * Callers render no Join affordance in that case.
 */
export function toMeetingHref(target: string): string | undefined {
  const trimmed = target.trim();
  if (!trimmed) return undefined;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('tel:')) {
    return trimmed;
  }

  // Reject anything else carrying a URL scheme. Without this, digit-stripping
  // turns `javascript:alert(1)` into `tel:1` — harmless in itself, but the
  // rule should be "links are http(s) or tel:", not "whatever survives having
  // its non-digits removed". Phone numbers never contain a colon.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return undefined;

  // Anything that isn't a link is treated as a phone number: strip spaces and
  // punctuation the dialer ignores, keeping a leading `+` for country codes.
  const digits = trimmed.replaceAll(/[^\d+]/g, '');

  // `+` alone is not a dialable number.
  return /\d/.test(digits) ? `tel:${digits}` : undefined;
}

/**
 * Open a meeting join target. A `tel:` href must navigate the current context
 * to hand off to the dialer; only web links open in a new tab.
 *
 * No-ops when the target isn't a usable link or phone number.
 */
export function openMeetingTarget(target: string): void {
  const href = toMeetingHref(target);
  if (!href) return;

  if (href.startsWith('tel:')) {
    globalThis.location.href = href;
    return;
  }

  openExternalUrl(href);
}
